/**
 * Session co-occurrence over ILO topics — the "topic links" layer.
 *
 * Input is the distinct (session, cluster) assignment pairs for one
 * clustering generation plus the cluster tree (clusterTree.js shape).
 * Output is two edge lists and their grouping:
 *
 *   • WORKFLOW edges — topic pairs that appear in the same sessions far
 *     more often than chance AND are semantically distant, i.e. real
 *     cross-topic work loops the embedding space cannot see.
 *   • MERGE edges — the same co-occurrence signal between topics that
 *     are ALSO close in the taxonomy: sessions testifying that two
 *     leaves are one activity the clusterer split.
 *
 * "Semantically distant" uses tree distance (hops to the lowest common
 * ancestor) as a proxy — the analytics API does not expose centroids,
 * and the taxonomy is itself embedding-derived, so near-in-tree is a
 * usable stand-in for near-in-meaning. Siblings / parent-child (hop sum
 * ≤ nearHops) count as near.
 *
 * Sessions spanning more than maxTopicsPerSession distinct topics are
 * excluded outright: one all-day session touching 90 topics contributes
 * ~4000 pairs of clique noise and no information (measured on staging —
 * including them collapses the graph into a single blob).
 *
 * Everything here is pure and synchronous; fetching lives in
 * topicFlowsData.js and the lens body.
 */

import { ANALYTICS_SUPPORTED_DIM_PLURALS } from "./featureFlags.js";

export const TOPIC_FLOW_DEFAULTS = Object.freeze({
    minSupport: 6,          // sessions containing both topics
    minLift: 8,             // co-occurrence ≥ this many × chance
    maxTopicsPerSession: 40,
    nearHops: 2,            // LCA hop-sum ≤ this ⇒ "near" ⇒ merge evidence
});

// rows [{session, clusterId}] → Map(session → Set(clusterId)), keeping
// only sessions with 2..maxTopicsPerSession distinct topics.
export function sessionTopicSets(rows, maxTopicsPerSession = TOPIC_FLOW_DEFAULTS.maxTopicsPerSession) {
    const bySession = new Map();
    for (const r of rows ?? []) {
        const session = r?.session;
        const clusterId = r?.clusterId != null ? String(r.clusterId) : "";
        if (!session || clusterId === "" || Number(clusterId) < 0) continue;
        let set = bySession.get(session);
        if (!set) { set = new Set(); bySession.set(session, set); }
        set.add(clusterId);
    }
    const kept = new Map();
    for (const [session, set] of bySession) {
        if (set.size >= 2 && set.size <= maxTopicsPerSession) kept.set(session, set);
    }
    return kept;
}

// Page size of the steered-session resolution (the lens resolves a PROMPT-
// level steer to session ids and intersects client-side — the pair query
// roots at the event-cluster entity and can't carry the steer). A FULL page
// means the steer matched more sessions than the lens can see; consumers
// must disclose that as under-reporting.
export const STEER_SESSION_CAP = 2000;

// How many session ids the pattern's "steer scope to these sessions" action
// writes into the query string — bounds steer/URL size; the label discloses
// the cap when it bites.
export const STEER_WRITE_CAP = 300;

// How the Topic patterns corpus honors the dashboard scope. The pair query
// roots at the event-cluster entity, which carries endpoint and session
// (notification) ids as first-class fields, so those steps push down into
// the query itself (`ecFilter`). Every other analytics-supported dim is
// PROMPT-level and rides the same client-side session-set intersection the
// steer uses (`needsResolution`, with `resolutionScope` as the resolution's
// parentScopePath — it carries ALL supported steps so the intersection is
// exact even where pushdown already applies). Dims the analytics graph
// cannot express land in `unappliedDims` for the header to disclose; they
// are never silently dropped and never fail the corpus closed.
export function topicScopePlan(scopePath) {
    const ecFilter = { endpoints: [], sessions: [] };
    const resolutionScope = [];
    const unappliedDims = [];
    let needsResolution = false;
    for (const step of Array.isArray(scopePath) ? scopePath : []) {
        const ids = (Array.isArray(step?.ids) && step.ids.length ? step.ids : step?.id != null ? [step.id] : [])
            .filter((x) => x != null && x !== "")
            .map(String);
        if (!ids.length) continue;
        if (!ANALYTICS_SUPPORTED_DIM_PLURALS.has(step.dim)) {
            if (!unappliedDims.includes(step.dim)) unappliedDims.push(step.dim);
            continue;
        }
        resolutionScope.push({ dim: step.dim, id: ids[0], ids });
        if (step.dim === "endpoints") ecFilter.endpoints.push(...ids);
        else if (step.dim === "sessions") ecFilter.sessions.push(...ids);
        else needsResolution = true;
    }
    return {
        scoped: resolutionScope.length > 0,
        ecFilter,
        resolutionScope,
        needsResolution,
        unappliedDims,
    };
}

// Bound on the session-id list a scoped per-topic evidence pull carries (an
// IN predicate on the traces query). When it binds, the sessions beyond it
// are simply not sampled for examples — consumers must disclose that beside
// the evidence (`capped` below), the same posture as the resolution cap.
export const TOPIC_EVIDENCE_SESSION_CAP = 1000;

// The corpus sessions that contain a topic — a SCOPED evidence pull fetches
// the topic's example prompts from these sessions instead of prefixing the
// ambient scope steps. Prefixing breaks at cluster scope: the topic step and
// the scope's own cluster step AND into two subtree predicates on the same
// event-cluster join, which no prompt can satisfy for unrelated topics. The
// corpus rows are already scope-enforced (pushdown and/or the session-set
// intersection), so their sessions ARE the scope at this lens's grain.
// `capped` is exact: true only when a further distinct session existed
// beyond the cap, never for a list that is merely full.
export function topicEvidenceSessions(rows, topicId, cap = TOPIC_EVIDENCE_SESSION_CAP) {
    const wanted = String(topicId);
    const sessions = [];
    const seen = new Set();
    for (const r of rows ?? []) {
        if (String(r?.clusterId) !== wanted || !r?.session) continue;
        const s = String(r.session);
        if (seen.has(s)) continue;
        if (sessions.length >= cap) return { sessions, capped: true };
        seen.add(s);
        sessions.push(s);
    }
    return { sessions, capped: false };
}

// Intersect pair rows with a resolved steered-session entry page
// ([{id}, …]). entries null/undefined = the steer is still resolving —
// rows come back null so the caller keeps its loading state up.
export function steeredPairRows(rows, entries) {
    if (!rows || !entries) return { rows: null, capped: false };
    const keep = new Set(entries.map((e) => String(e.id)));
    return {
        rows: rows.filter((r) => keep.has(String(r.session))),
        capped: entries.length >= STEER_SESSION_CAP,
    };
}

// The pattern-steer payload: the query-language clause (session ids capped
// at STEER_WRITE_CAP) and the button label that discloses the cap.
export function sessionSteer(sessions) {
    const all = sessions ?? [];
    const ids = all.slice(0, STEER_WRITE_CAP);
    const label = all.length > STEER_WRITE_CAP
        ? `steer scope to ${STEER_WRITE_CAP} of ${all.length.toLocaleString("en-US")} sessions`
        : `steer scope to these ${all.length} sessions`;
    return { query: `session:${ids.join(",")}`, label };
}

// rows → Map(session → endpoint id), first non-empty endpoint per session.
// Notification ids are endpoint-scoped, so multiple endpoints on one session
// would be a data quirk, not a real split — first wins.
export function sessionEndpoints(rows) {
    const bySession = new Map();
    for (const r of rows ?? []) {
        const session = r?.session;
        const endpoint = r?.endpoint != null ? String(r.endpoint) : "";
        if (!session || !endpoint || bySession.has(session)) continue;
        bySession.set(session, endpoint);
    }
    return bySession;
}

// Hop distance between two nodes through their lowest common ancestor,
// as { hops, relation }. null when either node is unknown or the nodes
// share no ancestor (disjoint roots — treated as far by the caller).
export function treeHopDistance(tree, a, b) {
    const chain = (id) => {
        const seen = [];
        let cur = tree?.byId?.get(String(id));
        const guard = new Set();
        while (cur && !guard.has(cur.id)) {
            guard.add(cur.id);
            seen.push(cur.id);
            cur = cur.parentId != null ? tree.byId.get(cur.parentId) : null;
        }
        return seen;
    };
    const ca = chain(a);
    const cb = chain(b);
    if (!ca.length || !cb.length) return null;
    const depthB = new Map(cb.map((id, i) => [id, i]));
    for (let i = 0; i < ca.length; i++) {
        const j = depthB.get(ca[i]);
        if (j !== undefined) {
            const hops = i + j;
            const relation = hops <= 1 ? "parent-child" : i === 1 && j === 1 ? "siblings" : "cousins";
            return { hops, relation };
        }
    }
    return null;
}

// One level of greedy modularity label-moving (Louvain's inner loop,
// iterated to a fixed point) over a weighted undirected graph given as
// edges [{a, b, w}]. Deterministic: nodes visited in sorted order.
// Returns Map(node → community label).
export function labelCommunities(edges) {
    const nodes = new Set();
    const adj = new Map();
    let total = 0;
    for (const e of edges) {
        nodes.add(e.a); nodes.add(e.b);
        total += e.w;
        if (!adj.has(e.a)) adj.set(e.a, new Map());
        if (!adj.has(e.b)) adj.set(e.b, new Map());
        adj.get(e.a).set(e.b, (adj.get(e.a).get(e.b) ?? 0) + e.w);
        adj.get(e.b).set(e.a, (adj.get(e.b).get(e.a) ?? 0) + e.w);
    }
    if (!nodes.size || total <= 0) return new Map();
    const degree = new Map();
    for (const n of nodes) {
        let d = 0;
        for (const w of adj.get(n).values()) d += w;
        degree.set(n, d);
    }
    const label = new Map([...nodes].sort().map((n) => [n, n]));
    const communityDegree = new Map(label);
    for (const n of nodes) communityDegree.set(n, degree.get(n));

    const m2 = 2 * total;
    let moved = true;
    let rounds = 0;
    while (moved && rounds < 20) {
        moved = false;
        rounds += 1;
        for (const n of [...nodes].sort()) {
            const cur = label.get(n);
            // Weight from n into each neighbouring community.
            const into = new Map();
            for (const [nb, w] of adj.get(n)) {
                const c = label.get(nb);
                into.set(c, (into.get(c) ?? 0) + w);
            }
            communityDegree.set(cur, communityDegree.get(cur) - degree.get(n));
            let best = cur;
            let bestGain = (into.get(cur) ?? 0) - (communityDegree.get(cur) * degree.get(n)) / m2;
            for (const [c, w] of into) {
                if (c === cur) continue;
                const gain = w - (communityDegree.get(c) * degree.get(n)) / m2;
                if (gain > bestGain + 1e-12 || (Math.abs(gain - bestGain) <= 1e-12 && String(c) < String(best))) {
                    best = c;
                    bestGain = gain;
                }
            }
            communityDegree.set(best, (communityDegree.get(best) ?? 0) + degree.get(n));
            if (best !== cur) { label.set(n, best); moved = true; }
        }
    }
    return label;
}

/**
 * The full computation. rows = [{session, clusterId}], tree = clusterTree
 * shape ({byId, childrenOf, roots}). Returns:
 *   { stats, workflowEdges, mergeEdges, archetypes }
 * Edges: { a, b, support, lift, hops, relation } with a/b = cluster id
 * strings; lift = observed co-occurrence over the independence baseline.
 * Archetypes: { id, leaves, edges, support } grouped from workflow edges,
 * ≥3 topics, ranked by internal session support.
 */
// The threshold-independent half of the computation: session topic-sets
// plus per-topic and per-pair session counts. O(sessions × topics²) — the
// expensive pass. Callers that re-run buildTopicFlows under different
// thresholds (the UI's chips) should build this ONCE and pass it via
// opts.index so a chip click only re-runs the cheap threshold/grouping
// stage.
export function indexTopicPairs(rows, maxTopicsPerSession = TOPIC_FLOW_DEFAULTS.maxTopicsPerSession) {
    const sessions = sessionTopicSets(rows, maxTopicsPerSession);
    const topicSessions = new Map();
    const pairCount = new Map();
    for (const set of sessions.values()) {
        const ids = [...set].sort();
        for (const id of ids) topicSessions.set(id, (topicSessions.get(id) ?? 0) + 1);
        for (let i = 0; i < ids.length; i++) {
            for (let j = i + 1; j < ids.length; j++) {
                const key = ids[i] + "|" + ids[j];
                pairCount.set(key, (pairCount.get(key) ?? 0) + 1);
            }
        }
    }
    // Every recurring pair (support ≥ 2), unfiltered by lift — the halo.
    // Patterns are where co-occurrence is CONCENTRATED, but most of a
    // topic's co-occurrence mass lives outside them (measured ~10x on
    // staging); consumers need the whole distribution to say so honestly.
    const pairSupports = new Map();
    for (const [key, support] of pairCount) {
        if (support >= 2) pairSupports.set(key, support);
    }
    return { sessions, topicSessions, pairCount, pairSupports };
}

export function buildTopicFlows(rows, tree, opts = {}) {
    const { minSupport, minLift, maxTopicsPerSession, nearHops } = { ...TOPIC_FLOW_DEFAULTS, ...opts };
    const { sessions, topicSessions, pairCount, pairSupports } =
        opts.index ?? indexTopicPairs(rows, maxTopicsPerSession);
    const nSessions = sessions.size;
    const empty = {
        stats: { sessionsUsed: nSessions, pairsConsidered: 0, workflowCount: 0, mergeCount: 0 },
        workflowEdges: [], mergeEdges: [], archetypes: [],
        topicSessions: new Map(),
        pairSupports: new Map(),
        sessionTopics: sessions,
    };
    if (nSessions < minSupport) return empty;

    const workflowEdges = [];
    const mergeEdges = [];
    for (const [key, support] of pairCount) {
        if (support < minSupport) continue;
        const [a, b] = key.split("|");
        const expected = (topicSessions.get(a) * topicSessions.get(b)) / nSessions;
        const lift = expected > 0 ? support / expected : 0;
        if (lift < minLift) continue;
        const dist = treeHopDistance(tree, a, b);
        const edge = {
            a, b, support,
            lift: Math.round(lift * 10) / 10,
            hops: dist ? dist.hops : null,
            relation: dist ? dist.relation : "unrelated",
        };
        if (dist && dist.hops <= nearHops) mergeEdges.push(edge);
        else workflowEdges.push(edge);
    }
    const strength = (e) => e.lift * Math.log1p(e.support);
    workflowEdges.sort((x, y) => strength(y) - strength(x));
    mergeEdges.sort((x, y) => strength(y) - strength(x));

    const label = labelCommunities(workflowEdges.map((e) => ({ a: e.a, b: e.b, w: strength(e) })));
    const groups = new Map();
    for (const e of workflowEdges) {
        if (label.get(e.a) !== label.get(e.b)) continue;
        const c = label.get(e.a);
        let g = groups.get(c);
        if (!g) { g = { leaves: new Set(), edges: [], support: 0 }; groups.set(c, g); }
        g.leaves.add(e.a); g.leaves.add(e.b);
        g.edges.push(e);
        g.support += e.support;
    }
    const archetypes = [...groups.entries()]
        .filter(([, g]) => g.leaves.size >= 3)
        .map(([id, g]) => ({
            id: String(id),
            leaves: [...g.leaves].sort((x, y) => (topicSessions.get(y) ?? 0) - (topicSessions.get(x) ?? 0)),
            edges: g.edges,
            support: g.support,
        }))
        .sort((x, y) => y.support - x.support);

    return {
        stats: {
            sessionsUsed: nSessions,
            pairsConsidered: pairCount.size,
            workflowCount: workflowEdges.length,
            mergeCount: mergeEdges.length,
        },
        workflowEdges,
        mergeEdges,
        archetypes,
        topicSessions,
        pairSupports,
        // The kept session → topic-set map, so consumers can slice sessions
        // by pattern membership (e.g. the endpoint-mix strip) without
        // re-deriving the mega-session exclusion.
        sessionTopics: sessions,
    };
}
